import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Spin, Alert, Row, Col, Statistic, Divider, Image, Modal, Space, message, Progress, Typography, Badge, Tooltip } from 'antd';
import { LineChartOutlined, CalculatorOutlined, BarChartOutlined, EyeOutlined, RocketOutlined, ThunderboltOutlined, StarOutlined, BulbOutlined, TrophyOutlined, DownloadOutlined } from '@ant-design/icons';
import { GetPredictionPlansApi, GetPredictionModelsApi, RunPredictionApi } from '../../api/prediction';
import { useSelector } from 'react-redux';
import jsPDF from 'jspdf';

const { Option } = Select;
const { Text, Title } = Typography;

function Prediction() {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [models, setModels] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [predictionResult, setPredictionResult] = useState(null);
  const [error, setError] = useState(null);
  const [previewModal, setPreviewModal] = useState({ visible: false, image: null, title: '' });
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [typingText, setTypingText] = useState('');
  
  // Get current performance cycle from Redux store
  const perfCycleState = useSelector((state) => state.perfCycleState.value);

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    if (perfCycleState?.id) {
      loadPlans(perfCycleState.id);
      setSelectedPlan(null);
      setSelectedPlanData(null);
    }
  }, [perfCycleState]);

  const handlePlanSelection = (planTitle) => {
    setSelectedPlan(planTitle);
    const planData = plans.find(plan => plan.title === planTitle);
    setSelectedPlanData(planData);
  };

  // Typing animation effect
  const typeMessage = (message, callback) => {
    setTypingText('');
    let i = 0;
    const timer = setInterval(() => {
      if (i < message.length) {
        setTypingText(message.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        if (callback) setTimeout(callback, 100);
      }
    }, 30);
    return timer;
  };

  // Loading messages with progress
  const loadingMessages = [
    { text: "Thinking about your plan...", progress: 10 },
    { text: "Analyzing historical data...", progress: 25 },
    { text: "Running AI calculations...", progress: 45 },
    { text: "Generating predictions...", progress: 65 },
    { text: "Creating beautiful charts...", progress: 80 },
    { text: "Finalizing results...", progress: 95 },
    { text: "Almost done! Don't worry, I'm pretty smart...", progress: 99 }
  ];

  const loadPlans = async (cycleId) => {
    setPlansLoading(true);
    try {
      const response = await GetPredictionPlansApi(cycleId);
      if (response.success) {
        setPlans(response.data);
      } else {
        setError('Failed to load plans');
      }
    } catch (error) {
      setError('Error loading plans: ' + error.message);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const response = await GetPredictionModelsApi();
      if (response.success) {
        setModels(response.data);
      } else {
        setError('Failed to load models');
      }
    } catch (error) {
      setError('Error loading models: ' + error.message);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!selectedPlan || !selectedModel) {
      setError('Please select both a plan and a model');
      return;
    }

    setLoading(true);
    setError(null);
    setPredictionResult(null);
    setLoadingProgress(0);

    // Initialize loading state
    setTypingText('');
    setLoadingProgress(0);
    
    // Animate through loading messages with controlled timing
    let messageIndex = 0;
    let messageTimer = null;
    
    const showNextMessage = () => {
      if (messageIndex < loadingMessages.length) {
        const currentMessage = loadingMessages[messageIndex];
        setLoadingProgress(currentMessage.progress);
        typeMessage(currentMessage.text, () => {
          messageIndex++;
          // Clear any existing timer
          if (messageTimer) clearTimeout(messageTimer);
          // Progressive delays to prevent rapid switching
          const delay = messageIndex <= 2 ? 1200 : 
                       messageIndex <= 4 ? 1800 : 2500;
          if (messageIndex < loadingMessages.length) {
            messageTimer = setTimeout(showNextMessage, delay);
          }
        });
      }
    };
    
    // Start the first message immediately
    setTimeout(showNextMessage, 100);

    try {
      const response = await RunPredictionApi(selectedPlan, selectedModel);
      if (response.success) {
        
        // Final success message
        setLoadingProgress(100);
        typeMessage("🎉 Prediction complete! Preparing your insights...", () => {
          setTimeout(() => {
            setPredictionResult(response.data);
            setLoading(false);
          }, 500);
        });
      } else {
        setError(response.message || 'Prediction calculation failed');
        setLoading(false);
      }
    } catch (error) {
      console.error('Prediction error:', error);
      setError('Error running prediction: ' + error.message);
      setLoading(false);
    }
  };

  const formatValueByScale = (value, scaleType, scaleUnit, symbol) => {
    if (!value || value === null || value === undefined) {
      return `${symbol || ''}0`;
    }

    // Convert value to number if it's a string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return `${symbol || ''}0`;
    }

    // Handle different scale types
    switch (scaleType?.toLowerCase()) {
      case 'percentage':
        // For percentage, the value is already in percentage form (e.g., 85 means 85%)
        return `${numValue.toFixed(1)}%`;
      
      case 'currency':
        if (numValue >= 1e6) {
          return `${symbol || '$'}${(numValue / 1e6).toFixed(1)}M`;
        } else if (numValue >= 1e3) {
          return `${symbol || '$'}${(numValue / 1e3).toFixed(0)}K`;
        }
        return `${symbol || '$'}${numValue.toLocaleString()}`;
      
      case 'time':
        return `${numValue.toLocaleString()} ${symbol || scaleUnit || ''}`;
      
      case 'number':
        if (numValue >= 1e6) {
          return `${(numValue / 1e6).toFixed(1)}M`;
        } else if (numValue >= 1e3) {
          return `${(numValue / 1e3).toFixed(0)}K`;
        }
        return numValue.toLocaleString();
      
      default:
        // If no specific scale type, try to format intelligently
        if (numValue >= 1e6) {
          return `${(numValue / 1e6).toFixed(1)}M ${symbol || scaleUnit || ''}`;
        } else if (numValue >= 1e3) {
          return `${(numValue / 1e3).toFixed(0)}K ${symbol || scaleUnit || ''}`;
        }
        return `${numValue.toLocaleString()} ${symbol || scaleUnit || ''}`;
    }
  };

  const formatPercentage = (value) => {
    // Check if value is already in percentage form (0-100) or decimal form (0-1)
    if (value <= 1) {
      // Value is in decimal form (0-1), convert to percentage
      return `${(value * 100).toFixed(1)}%`;
    } else {
      // Value is already in percentage form (0-100)
      return `${value.toFixed(1)}%`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Ahead of Schedule':
        return '#52c41a';
      case 'On Track':
        return '#1890ff';
      case 'Behind Schedule':
        return '#faad14';
      default:
        return '#d9d9d9';
    }
  };

  const showChartPreview = (base64Image, title) => {
    setPreviewModal({
      visible: true,
      image: base64Image,
      title: title
    });
  };

  const downloadPDFReport = async () => {
    if (!predictionResult) {
      message.error('No prediction data available for download');
      return;
    }

    try {
      message.loading('Generating PDF report...', 0);
      
      // Add a small delay to prevent UI blocking
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const doc = new jsPDF();
      
      // PAGE 1: Title and Plan Information
      doc.setFontSize(24);
      doc.setTextColor(6, 78, 59);
      doc.text('Prediction & Forecasting Report', 105, 30, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 45, { align: 'center' });
      
      // Add a line separator
      doc.setDrawColor(6, 78, 59);
      doc.setLineWidth(0.5);
      doc.line(20, 55, 190, 55);
      
      doc.setFontSize(14);
      doc.setTextColor(6, 78, 59);
      doc.text('Plan Information', 20, 75);
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Plan Title: ${selectedPlan}`, 20, 90);
      doc.text(`Analysis Model: ${selectedModel}`, 20, 100);
      doc.text(`Performance Cycle: ${perfCycleState?.name || 'Not specified'}`, 20, 110);
      doc.text(`Model Used: ${predictionResult.model_used === 'rfr' ? 'Revenue Forecast' : 
                predictionResult.model_used === 'linear' ? 'Linear Trend' : 
                predictionResult.model_used === 'ensemble' ? 'Ensemble' : 
                predictionResult.model_used}`, 20, 120);
      doc.text(`Plan Type: ${predictionResult.has_child_tasks ? 'Hierarchical Plan (with child tasks)' : 'Single Plan'}`, 20, 130);
      
      // PAGE 2: Executive Summary
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(6, 78, 59);
      doc.text('Executive Summary', 105, 30, { align: 'center' });
      
      // Create summary cards as rectangles
      const cardWidth = 80;
      const cardHeight = 30;
      const startX = 20;
      const startY = 50;
      
      // Card 1: Current Progress
      doc.setFillColor(239, 246, 255);
      doc.rect(startX, startY, cardWidth, cardHeight, 'F');
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(10);
      doc.text('Current Progress', startX + 5, startY + 8);
      doc.setFontSize(12);
      doc.text(formatValueByScale(
        predictionResult.summary?.current_progress || 0,
        selectedPlanData?.scale_name,
        selectedPlanData?.scale_unit_name,
        selectedPlanData?.scale_unit_symbol
      ), startX + 5, startY + 20);
      
      // Card 2: Predicted Final
      doc.setFillColor(240, 253, 244);
      doc.rect(startX + cardWidth + 5, startY, cardWidth, cardHeight, 'F');
      doc.setTextColor(22, 101, 52);
      doc.setFontSize(10);
      doc.text('Predicted Final', startX + cardWidth + 10, startY + 8);
      doc.setFontSize(12);
      doc.text(formatValueByScale(
        predictionResult.summary?.predicted_final || 0,
        selectedPlanData?.scale_name,
        selectedPlanData?.scale_unit_name,
        selectedPlanData?.scale_unit_symbol
      ), startX + cardWidth + 10, startY + 20);
      
      // Card 3: Achievement Probability
      doc.setFillColor(250, 245, 255);
      doc.rect(startX, startY + cardHeight + 5, cardWidth, cardHeight, 'F');
      doc.setTextColor(124, 58, 237);
      doc.setFontSize(10);
      doc.text('Achievement Probability', startX + 5, startY + cardHeight + 13);
      doc.setFontSize(12);
      doc.text(formatPercentage(predictionResult.summary?.achievement_probability || 0), startX + 5, startY + cardHeight + 25);
      
      // Card 4: Timeline Status
      doc.setFillColor(255, 251, 235);
      doc.rect(startX + cardWidth + 5, startY + cardHeight + 5, cardWidth, cardHeight, 'F');
      doc.setTextColor(217, 119, 6);
      doc.setFontSize(10);
      doc.text('Timeline Status', startX + cardWidth + 10, startY + cardHeight + 13);
      doc.setFontSize(12);
      doc.text(predictionResult.summary?.timeline_status || 'Unknown', startX + cardWidth + 10, startY + cardHeight + 25);
      
      // PAGE 3: Timeline Analysis
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(6, 78, 59);
      doc.text('Timeline Analysis', 105, 30, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      
      const timelineItems = [
        { label: 'Target Date', value: predictionResult.summary?.target_date ? new Date(predictionResult.summary.target_date).toLocaleDateString() : 'Not set' },
        { label: 'Projected Completion', value: predictionResult.summary?.projected_completion_date ? new Date(predictionResult.summary.projected_completion_date).toLocaleDateString() : 'Not available' },
        { label: 'Days Ahead/Behind', value: predictionResult.summary?.days_ahead_behind ? 
          `${Math.abs(predictionResult.summary.days_ahead_behind)} days ${predictionResult.summary.days_ahead_behind < 0 ? 'ahead' : 'behind'}` : 'Not available' },
        { label: 'Gap to Target', value: (() => {
          const gap = predictionResult.summary?.gap_to_target ?? 
            ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
          if (gap <= 0) {
            return `Exceeds target by ${formatValueByScale(Math.abs(gap), selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}`;
          }
          return `${formatValueByScale(gap, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} remaining`;
        })() },
        { label: 'Performance Trend', value: predictionResult.summary?.performance_trend || 'Unknown' },
        { label: 'Confidence Level', value: formatPercentage(predictionResult.summary?.confidence_level || 0) }
      ];
      
      let timelineYPos = 60;
      timelineItems.forEach(item => {
        doc.setFontSize(11);
        doc.setTextColor(6, 78, 59);
        doc.text(`${item.label}:`, 20, timelineYPos);
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(item.value, 80, timelineYPos);
        timelineYPos += 15;
      });
      
      // Add quarterly breakdown if available
      if (predictionResult.summary?.quarterly_summary && predictionResult.summary.quarterly_summary.length > 0) {
        doc.addPage();
        doc.setFontSize(20);
        doc.setTextColor(6, 78, 59);
        doc.text('Quarterly Breakdown', 105, 30, { align: 'center' });
        
        // Create a proper table
        const tableHeaders = ['Quarter', 'Status', 'Current', 'Target', 'Completion %'];
        const tableData = predictionResult.summary.quarterly_summary.map(quarter => [
          quarter.quarter,
          quarter.status,
          formatValueByScale(quarter.current_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol),
          formatValueByScale(quarter.target_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol),
          formatPercentage(quarter.completion_pct)
        ]);
        
        let tableY = 50;
        const colWidths = [30, 40, 35, 35, 30];
        const startX = 20;
        
        // Draw headers
        doc.setFillColor(6, 78, 59);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        
        let currentX = startX;
        tableHeaders.forEach((header, index) => {
          doc.rect(currentX, tableY, colWidths[index], 10, 'F');
          doc.text(header, currentX + 2, tableY + 7);
          currentX += colWidths[index];
        });
        
        // Draw data rows
        doc.setTextColor(0, 0, 0);
        tableY += 10;
        
        tableData.forEach((row, rowIndex) => {
          if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 250, 252);
            doc.rect(startX, tableY, colWidths.reduce((a, b) => a + b, 0), 10, 'F');
          }
          
          let currentX = startX;
          row.forEach((cell, cellIndex) => {
            doc.text(String(cell), currentX + 2, tableY + 7);
            currentX += colWidths[cellIndex];
          });
          tableY += 10;
        });
      }
      
      // Note about charts (removing chart embedding to prevent crashes)
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(6, 78, 59);
      doc.text('Charts & Visualizations', 105, 30, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text('This report includes the following visualizations:', 20, 60);
      
      if (predictionResult.charts?.timeline_chart) {
        doc.text('• Timeline Forecast Chart - Available in the web interface', 25, 75);
      }
      
      if (predictionResult.charts?.quarterly_progression_chart) {
        doc.text('• Quarterly Analysis Chart - Available in the web interface', 25, 90);
      }
      
      doc.text('Note: For the best viewing experience of charts and graphs,', 20, 120);
      doc.text('please refer to the interactive web interface.', 20, 135);
      
      // Add Risk Assessment Page
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(6, 78, 59);
      doc.text('Risk Assessment & Recommendations', 105, 30, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setTextColor(6, 78, 59);
      doc.text('Achievement Risk Analysis', 20, 55);
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const achievementProb = predictionResult.summary?.achievement_probability || 0;
      const riskLevel = achievementProb > 0.8 ? 'Low Risk' : achievementProb > 0.6 ? 'Medium Risk' : 'High Risk';
      const riskColor = achievementProb > 0.8 ? [34, 197, 94] : achievementProb > 0.6 ? [251, 191, 36] : [239, 68, 68];
      
      doc.text(`Achievement Probability: ${formatPercentage(achievementProb)}`, 20, 70);
      doc.setTextColor(...riskColor);
      doc.text(`Risk Level: ${riskLevel}`, 20, 80);
      doc.setTextColor(0, 0, 0);
      const gap = predictionResult.summary?.gap_to_target ?? 
        ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
      const gapText = gap <= 0 
        ? `Exceeds target by ${formatValueByScale(Math.abs(gap), selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}`
        : `${formatValueByScale(gap, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} remaining`;
      doc.text(`Gap to Target: ${gapText}`, 20, 90);
      
      doc.setFontSize(14);
      doc.setTextColor(6, 78, 59);
      doc.text('Timeline Risk Analysis', 20, 110);
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      const timelineStatus = predictionResult.summary?.timeline_status || 'Unknown';
      const timelineColor = timelineStatus === 'Ahead of Schedule' ? [34, 197, 94] : 
                           timelineStatus === 'On Track' ? [59, 130, 246] : [251, 191, 36];
      
      doc.text(`Timeline Status: ${timelineStatus}`, 20, 125);
      doc.text(`Days Difference: ${predictionResult.summary?.days_ahead_behind ? 
               `${Math.abs(predictionResult.summary.days_ahead_behind)} days ${predictionResult.summary.days_ahead_behind < 0 ? 'ahead' : 'behind'}` : 
               'Not available'}`, 20, 135);
      
      const recommendation = timelineStatus === 'Ahead of Schedule' ? 'Maintain current pace and consider resource optimization' :
                            timelineStatus === 'On Track' ? 'Continue monitoring progress and maintain current strategies' : 
                            'Consider acceleration strategies and additional resource allocation';
      doc.text(`Recommendation: ${recommendation}`, 20, 145);
      
      // Add Performance Insights Page
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(6, 78, 59);
      doc.text('Performance Insights & Analysis', 105, 30, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setTextColor(6, 78, 59);
      doc.text('Key Performance Indicators', 20, 55);
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      let yPos = 70;
      
      const insights = [
        { label: 'Current Performance Rate', value: `${((predictionResult.summary?.current_progress || 0) / (predictionResult.summary?.main_target || 1) * 100).toFixed(1)}% of target achieved` },
        { label: 'Projected Success Rate', value: formatPercentage(predictionResult.summary?.achievement_probability || 0) },
        { label: 'Performance Velocity', value: predictionResult.summary?.performance_trend || 'Steady' },
        { label: 'Model Confidence', value: formatPercentage(predictionResult.summary?.confidence_level || 0) },
        { label: 'Completion Forecast', value: predictionResult.summary?.projected_completion_date ? new Date(predictionResult.summary.projected_completion_date).toLocaleDateString() : 'Not available' },
        { label: 'Target Achievement Gap', value: (() => {
          const gap = predictionResult.summary?.gap_to_target ?? 
            ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
          if (gap <= 0) {
            return `Exceeds by ${formatValueByScale(Math.abs(gap), selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}`;
          }
          return `${formatValueByScale(gap, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} needed`;
        })() }
      ];
      
      insights.forEach(insight => {
        doc.setFontSize(10);
        doc.setTextColor(6, 78, 59);
        doc.text(`${insight.label}:`, 20, yPos);
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(insight.value, 80, yPos);
        yPos += 12;
      });
      
      // Add Strategic Recommendations
      doc.setFontSize(14);
      doc.setTextColor(6, 78, 59);
      doc.text('Strategic Recommendations', 20, yPos + 10);
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      yPos += 25;
      
      const recommendations = [];
      
      if (achievementProb < 0.7) {
        recommendations.push('• Consider increasing resource allocation to improve achievement probability');
        recommendations.push('• Review current strategies and identify potential bottlenecks');
        recommendations.push('• Implement more frequent progress monitoring and course corrections');
      } else if (achievementProb > 0.9) {
        recommendations.push('• Current trajectory is excellent - maintain existing strategies');
        recommendations.push('• Consider setting more ambitious targets for future cycles');
        recommendations.push('• Document successful practices for replication');
      } else {
        recommendations.push('• Performance is on track - continue current approach');
        recommendations.push('• Monitor for any emerging risks or opportunities');
        recommendations.push('• Consider minor optimizations to improve efficiency');
      }
      
      if (predictionResult.summary?.days_ahead_behind > 5) {
        recommendations.push('• Timeline is behind schedule - consider acceleration measures');
        recommendations.push('• Review critical path activities and resource constraints');
      } else if (predictionResult.summary?.days_ahead_behind < -5) {
        recommendations.push('• Timeline is ahead of schedule - excellent progress');
        recommendations.push('• Consider reallocating resources to other priorities');
      }
      
      recommendations.forEach(rec => {
        doc.text(rec, 20, yPos);
        yPos += 8;
      });
      
      // Save the PDF
      doc.save(`prediction-report-${selectedPlan}-${new Date().toISOString().split('T')[0]}.pdf`);
      
      // Clear loading message and show success
      message.destroy();
      message.success('PDF report downloaded successfully!');
      
    } catch (error) {
      console.error('PDF generation error:', error);
      message.destroy();
      message.error('Failed to download PDF report. Please try again.');
    }
  };

  return (
    <div className="p-6" style={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <div className="mb-6">
        <Card style={{ 
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e8eaed',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Title level={1} style={{ 
              color: '#1f2937',
              marginBottom: '10px'
            }}>
              <RocketOutlined style={{ marginRight: '15px', color: '#3b82f6' }} />
              Prediction & Forecasting
            </Title>
            <Text style={{ fontSize: '16px', color: '#6b7280' }}>
              Generate revenue forecasts and timeline predictions using advanced machine learning models
            </Text>
          </div>
        </Card>
      </div>

      {/* Configuration Section */}
      <Card 
        title={
          <span style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>
            <BulbOutlined style={{ marginRight: '10px', color: '#3b82f6' }} />
            Prediction Configuration
          </span>
        }
        style={{ 
          marginBottom: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Row gutter={[24, 24]}>
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong style={{ fontSize: '16px' }}>Performance Cycle</Text>
            </div>
            <Card size="small" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Text style={{ color: '#475569', fontWeight: '500' }}>
                {perfCycleState?.name || 'No cycle selected'}
              </Text>
            </Card>
          </Col>
          
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong style={{ fontSize: '16px' }}>Select Plan</Text>
            </div>
            <Select
              placeholder={plansLoading ? "Loading plans..." : "Choose a plan to analyze"}
              style={{ width: '100%', height: '50px' }}
              value={selectedPlan}
              onChange={handlePlanSelection}
              size="large"
              loading={plansLoading}
              disabled={plansLoading}
              notFoundContent={plansLoading ? <Spin size="small" /> : 'No plans found'}
            >
              {plans.map((plan) => (
                <Option key={plan.title} value={plan.title}>
                  <div>
                    <div className="font-medium" style={{ fontSize: '14px' }}>{plan.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Target: {formatValueByScale(
                        plan.target, 
                        plan.scale_name || plan.scaleName || plan.scale_type, 
                        plan.scale_unit_name || plan.scaleUnitName || plan.scale_unit, 
                        plan.scale_unit_symbol || plan.scaleUnitSymbol || plan.symbol
                      )}
                      {(plan.scale_name || plan.scaleName) && (
                        <Badge 
                          count={plan.scale_name || plan.scaleName} 
                          style={{ 
                            backgroundColor: '#52c41a', 
                            marginLeft: '8px',
                            fontSize: '10px'
                          }} 
                        />
                      )}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong style={{ fontSize: '16px' }}>Select Analysis Model</Text>
            </div>
            <Select
              placeholder={modelsLoading ? "Loading models..." : "Choose prediction model"}
              style={{ width: '100%', height: '50px' }}
              value={selectedModel}
              onChange={setSelectedModel}
              size="large"
              loading={modelsLoading}
              disabled={modelsLoading}
              notFoundContent={modelsLoading ? <Spin size="small" /> : 'No models found'}
            >
              {models.map((model) => (
                <Option key={model.name} value={model.name}>
                  {model.display_name || model.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Divider />

        <div style={{ textAlign: 'center', position: 'relative' }}>
          <Button
            type="primary"
            size="large"
            icon={<CalculatorOutlined />}
            onClick={handleCalculate}
            loading={loading}
            disabled={!selectedPlan || !selectedModel}
            className="matrix-button"
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: '500',
              borderRadius: '8px',
              backgroundColor: '#064e3b',
              borderColor: '#064e3b',
              boxShadow: '0 2px 4px rgba(6, 78, 59, 0.4)',
              minWidth: '180px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {loading ? 'Calculating...' : 'Generate Prediction'}
          </Button>
        </div>
      </Card>

      {/* Loading Animation */}
      {loading && (
        <Card style={{ 
          marginBottom: '24px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          backgroundColor: 'white'
        }}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" style={{ marginBottom: '20px' }} />
            <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '15px', color: '#374151' }}>
              {typingText}
              <span style={{ animation: 'blink 1s infinite' }}>|</span>
            </div>
            <Progress 
              percent={loadingProgress} 
              strokeColor={{
                '0%': '#22c55e',
                '50%': '#16a34a',
                '100%': '#064e3b',
              }}
              trailColor="#e5e7eb"
              strokeWidth={8}
              style={{ marginBottom: '10px' }}
            />
            <Text style={{ color: '#6b7280' }}>
              Hang tight! Our AI is working its magic...
            </Text>
          </div>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Alert
          message="Prediction Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '24px', borderRadius: '10px' }}
          action={
            <Button size="small" danger onClick={() => setError(null)}>
              Dismiss
            </Button>
          }
        />
      )}

      {/* Results Section */}
      {predictionResult && (
        <>
          {/* Summary Statistics */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                  <TrophyOutlined style={{ marginRight: '10px', color: '#3b82f6' }} />
                  Prediction Results
                </span>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={downloadPDFReport}
                  style={{
                    backgroundColor: '#064e3b',
                    borderColor: '#064e3b',
                    borderRadius: '6px'
                  }}
                >
                  Download PDF Report
                </Button>
              </div>
            }
            style={{ 
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Row gutter={16} className="mb-6">
              <Col span={6}>
                <Card style={{ 
                  backgroundColor: '#eff6ff',
                  border: '1px solid #dbeafe',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ color: '#1e40af', fontSize: '14px', fontWeight: '500' }}>Current Progress</span>}
                    value={formatValueByScale(
                      predictionResult.summary?.current_progress || 0,
                      selectedPlanData?.scale_name,
                      selectedPlanData?.scale_unit_name,
                      selectedPlanData?.scale_unit_symbol
                    )}
                    valueStyle={{ color: '#1e40af', fontSize: '20px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card style={{ 
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #dcfce7',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ color: '#166534', fontSize: '14px', fontWeight: '500' }}>Predicted Final</span>}
                    value={formatValueByScale(
                      predictionResult.summary?.predicted_final || 0,
                      selectedPlanData?.scale_name,
                      selectedPlanData?.scale_unit_name,
                      selectedPlanData?.scale_unit_symbol
                    )}
                    valueStyle={{ color: '#166534', fontSize: '20px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card style={{ 
                  backgroundColor: '#faf5ff',
                  border: '1px solid #e9d5ff',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ color: '#7c3aed', fontSize: '14px', fontWeight: '500' }}>Achievement Probability</span>}
                    value={formatPercentage(predictionResult.summary?.achievement_probability || 0)}
                    valueStyle={{ color: '#7c3aed', fontSize: '20px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card style={{ 
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <Statistic
                    title={<span style={{ color: '#d97706', fontSize: '14px', fontWeight: '500' }}>Timeline Status</span>}
                    value={predictionResult.summary?.timeline_status || 'Unknown'}
                    valueStyle={{ color: '#d97706', fontSize: '18px', fontWeight: '600' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Progress Assessment Banner */}
          {predictionResult.summary?.progress_assessment && (
            <Alert
              message={
                <span style={{ fontSize: '15px', fontWeight: '600' }}>
                  {(() => {
                    const prob = predictionResult.summary?.achievement_probability || 0;
                    if (prob >= 0.80) return '✅ Plan Health: Strong';
                    if (prob >= 0.60) return '⚠️ Plan Health: Moderate';
                    if (prob >= 0.40) return '🔶 Plan Health: At Risk';
                    return '🚨 Plan Health: Critical';
                  })()}
                </span>
              }
              description={
                <span style={{ fontSize: '14px' }}>
                  {predictionResult.summary.progress_assessment}
                </span>
              }
              type={(() => {
                const prob = predictionResult.summary?.achievement_probability || 0;
                if (prob >= 0.80) return 'success';
                if (prob >= 0.60) return 'warning';
                return 'error';
              })()}
              showIcon
              style={{ marginBottom: '24px', borderRadius: '10px', fontSize: '14px' }}
            />
          )}

          {/* Charts Section */}
          <Row gutter={16} className="mb-6">
            {predictionResult.charts?.timeline_chart && (
              <Col span={12}>
                <Card
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      <LineChartOutlined style={{ marginRight: '8px', color: '#3b82f6' }} />
                      Timeline Forecast
                    </span>
                  }
                  style={{ 
                    borderRadius: '12px', 
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                  actions={[
                    <Tooltip title="View Full Size">
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => showChartPreview(predictionResult.charts.timeline_chart, 'Timeline Forecast')}
                        style={{ color: '#3b82f6' }}
                      >
                        Preview
                      </Button>
                    </Tooltip>
                  ]}
                  bodyStyle={{ padding: '4px', margin: 0 }}
                >
                  <div style={{ 
                    textAlign: 'center', 
                    lineHeight: 0,
                    margin: 0,
                    padding: 0,
                    height: 'auto',
                    overflow: 'hidden'
                  }}>
                    <Image
                      src={`data:image/png;base64,${predictionResult.charts.timeline_chart}`}
                      alt="Timeline Forecast Chart"
                      style={{ 
                        width: '100%', 
                        height: 'auto',
                        borderRadius: '8px',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        verticalAlign: 'top'
                      }}
                      preview={false}
                    />
                  </div>
                </Card>
              </Col>
            )}

            {predictionResult.charts?.quarterly_progression_chart && (
              <Col span={12}>
                <div style={{ 
                  borderRadius: '12px', 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}>
                  <div style={{ 
                    padding: '16px 24px 12px', 
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      <BarChartOutlined style={{ marginRight: '8px', color: '#10b981' }} />
                      Quarterly Analysis
                    </span>
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => showChartPreview(predictionResult.charts.quarterly_progression_chart, 'Quarterly Analysis')}
                      style={{ color: '#10b981', padding: '0' }}
                    >
                      Preview
                    </Button>
                  </div>
                  <img
                    src={`data:image/png;base64,${predictionResult.charts.quarterly_progression_chart}`}
                    alt="Quarterly Progression Chart"
                    style={{
                      width: '100%',
                      height: 'auto',
                      minHeight: '400px',
                      maxHeight: '600px',
                      objectFit: 'contain',
                      margin: '0',
                      padding: '0',
                      display: 'block',
                      border: 'none'
                    }}
                  />
                </div>
              </Col>
            )}
          </Row>

          {/* Timeline Analysis Section */}
          <Row gutter={16} className="mb-6">
            <Col span={24}>
              <Card 
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Timeline Analysis
                  </span>
                }
                style={{ 
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic
                      title="Target Date"
                      value={predictionResult.summary?.target_date ? new Date(predictionResult.summary.target_date).toLocaleDateString() : 'Not set'}
                      valueStyle={{ fontSize: '14px', color: '#374151' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Projected Completion"
                      value={predictionResult.summary?.projected_completion_date ? new Date(predictionResult.summary.projected_completion_date).toLocaleDateString() : 'Not available'}
                      valueStyle={{ fontSize: '14px', color: '#374151' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title={predictionResult.summary?.days_ahead_behind < 0 ? "Days Ahead" : 
                            predictionResult.summary?.days_ahead_behind > 0 ? "Days Behind" : "Days Ahead/Behind"}
                      value={predictionResult.summary?.days_ahead_behind ? 
                             `${Math.abs(predictionResult.summary.days_ahead_behind)} days ${predictionResult.summary.days_ahead_behind < 0 ? 'ahead' : 'behind'}` : 
                             'Not available'}
                      valueStyle={{ 
                        color: predictionResult.summary?.days_ahead_behind < 0 ? '#10b981' : 
                               predictionResult.summary?.days_ahead_behind > 0 ? '#f59e0b' : '#6b7280',
                        fontSize: '14px'
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="Gap to Target"
                      value={(() => {
                        const gap = predictionResult.summary?.gap_to_target ?? 
                          ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
                        if (gap <= 0) {
                          return formatValueByScale(
                            Math.abs(gap),
                            selectedPlanData?.scale_name,
                            selectedPlanData?.scale_unit_name,
                            selectedPlanData?.scale_unit_symbol
                          ) + ' ahead';
                        }
                        return formatValueByScale(
                          gap,
                          selectedPlanData?.scale_name,
                          selectedPlanData?.scale_unit_name,
                          selectedPlanData?.scale_unit_symbol
                        ) + ' remaining';
                      })()}
                      valueStyle={{ 
                        color: (() => {
                          const gap = predictionResult.summary?.gap_to_target ?? 
                            ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
                          return gap <= 0 ? '#10b981' : '#f59e0b';
                        })(),
                        fontSize: '14px'
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Quarterly Performance Breakdown */}
          {predictionResult.summary?.quarterly_summary && predictionResult.summary.quarterly_summary.length > 0 && (
            <Row gutter={16} className="mb-6">
              <Col span={24}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      Quarterly Performance Breakdown
                    </span>
                  }
                  style={{ 
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Row gutter={16}>
                    {predictionResult.summary.quarterly_summary.map((quarter, index) => (
                      <Col span={4} key={index}>
                        <Card size="small" style={{ 
                          textAlign: 'center',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}>
                          <div style={{ fontWeight: '600', color: '#3b82f6', marginBottom: '8px' }}>
                            {quarter.quarter}
                          </div>
                          <div style={{ 
                            color: quarter.status === 'Complete' ? '#10b981' : 
                                   quarter.status === 'In Progress' ? '#f59e0b' : '#6b7280',
                            marginBottom: '4px',
                            fontSize: '12px'
                          }}>
                            {quarter.status}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {formatValueByScale(quarter.current_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} / {formatValueByScale(quarter.target_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {formatPercentage(quarter.completion_pct)}
                          </div>
                          <div style={{ 
                            fontSize: '10px', 
                            color: quarter.performance_rating === 'Fast' ? '#10b981' : 
                                   quarter.performance_rating === 'On Pace' ? '#3b82f6' : '#f59e0b'
                          }}>
                            {quarter.performance_rating}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          {/* Risk Assessment Section */}
          <Row gutter={16} className="mb-6">
            <Col span={24}>
              <Card 
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    Risk Assessment & Recommendations
                  </span>
                }
                style={{ 
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Alert
                      message="Achievement Risk"
                      description={
                        <div>
                          <p><strong>Probability:</strong> {formatPercentage(predictionResult.summary?.achievement_probability || 0)}</p>
                          <p><strong>Risk Level:</strong> {
                            (predictionResult.summary?.achievement_probability || 0) > 0.8 ? 'Low' :
                            (predictionResult.summary?.achievement_probability || 0) > 0.6 ? 'Medium' : 'High'
                          }</p>
                          <p><strong>Gap:</strong> {(() => {
                            const gap = predictionResult.summary?.gap_to_target ?? 
                              ((predictionResult.summary?.main_target || 0) - (predictionResult.summary?.predicted_final || 0));
                            if (gap <= 0) {
                              return `Exceeds target by ${formatValueByScale(Math.abs(gap), selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}`;
                            }
                            return `${formatValueByScale(gap, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} still needed`;
                          })()}</p>
                        </div>
                      }
                      type={
                        (predictionResult.summary?.achievement_probability || 0) > 0.8 ? 'success' :
                        (predictionResult.summary?.achievement_probability || 0) > 0.6 ? 'warning' : 'error'
                      }
                      showIcon
                      style={{ borderRadius: '8px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Alert
                      message="Timeline Risk"
                      description={
                        <div>
                          <p><strong>Status:</strong> {predictionResult.summary?.timeline_status || 'Unknown'}</p>
                          <p><strong>Days Difference:</strong> {predictionResult.summary?.days_ahead_behind ? 
                             `${Math.abs(predictionResult.summary.days_ahead_behind)} days ${predictionResult.summary.days_ahead_behind < 0 ? 'ahead' : 'behind'}` : 
                             'Not available'}</p>
                          <p><strong>Recommendation:</strong> {
                            predictionResult.summary?.timeline_status === 'Ahead of Schedule' ? 'Maintain current pace' :
                            predictionResult.summary?.timeline_status === 'On Track' ? 'Continue monitoring' : 'Consider acceleration strategies'
                          }</p>
                        </div>
                      }
                      type={
                        predictionResult.summary?.timeline_status === 'Ahead of Schedule' ? 'success' :
                        predictionResult.summary?.timeline_status === 'On Track' ? 'info' : 'warning'
                      }
                      showIcon
                      style={{ borderRadius: '8px' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Performance Insights Section */}
          <Row gutter={16} className="mb-6">
            <Col span={24}>
              <Card 
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                    <StarOutlined style={{ marginRight: '10px', color: '#7c3aed' }} />
                    Performance Insights & Predictions
                  </span>
                }
                style={{ 
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Card size="small" style={{ 
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <Statistic
                        title="Current Performance Rate"
                        value={`${((predictionResult.summary?.current_progress || 0) / (predictionResult.summary?.main_target || 1) * 100).toFixed(1)}%`}
                        suffix="of target"
                        valueStyle={{ color: '#0369a1', fontSize: '16px', fontWeight: '600' }}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ 
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <Statistic
                        title="Performance Velocity"
                        value={predictionResult.summary?.performance_trend || 'Steady'}
                        valueStyle={{ color: '#166534', fontSize: '16px', fontWeight: '600' }}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ 
                      backgroundColor: '#fefce8',
                      border: '1px solid #fef08a',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <Statistic
                        title="Model Accuracy"
                        value={formatPercentage(predictionResult.summary?.confidence_level || 0)}
                        valueStyle={{ color: '#ca8a04', fontSize: '16px', fontWeight: '600' }}
                      />
                    </Card>
                  </Col>
                </Row>
                
                <Divider style={{ margin: '16px 0' }} />
                
                <Row gutter={16} justify="center">
                  <Col span={12}>
                    <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#374151', textAlign: 'center' }}>Target Achievement Probability</h4>
                    <Progress 
                      percent={Math.round((predictionResult.summary?.achievement_probability || 0) * 100)}
                      strokeColor={{
                        '0%': '#ef4444',
                        '70%': '#f59e0b',
                        '100%': '#10b981',
                      }}
                      format={(percent) => `${percent}% Likely`}
                      strokeWidth={12}
                      style={{ fontSize: '12px' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Advanced Analytics Section */}
          {predictionResult.summary && (
            <Row gutter={16} className="mb-6">
              <Col span={12}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      Predictive Analytics
                    </span>
                  }
                  style={{ 
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#064e3b' }}>Forecast Accuracy:</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Badge 
                        status={
                          (predictionResult.summary?.confidence_level || 0) > 0.8 ? 'success' :
                          (predictionResult.summary?.confidence_level || 0) > 0.6 ? 'warning' : 'error'
                        }
                        text={
                          (predictionResult.summary?.confidence_level || 0) > 0.8 ? 'High Confidence' :
                          (predictionResult.summary?.confidence_level || 0) > 0.6 ? 'Medium Confidence' : 'Low Confidence'
                        }
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#064e3b' }}>Performance Pattern:</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Text>{predictionResult.summary?.performance_trend || 'Steady progression'}</Text>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#064e3b' }}>Completion Forecast:</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Text>
                        {predictionResult.summary?.projected_completion_date ? 
                          new Date(predictionResult.summary.projected_completion_date).toLocaleDateString() : 
                          'Date not available'
                        }
                      </Text>
                    </div>
                  </div>
                  
                  <div>
                    <Text strong style={{ color: '#064e3b' }}>Risk Level:</Text>
                    <div style={{ marginTop: '4px' }}>
                      <Badge 
                        status={
                          (predictionResult.summary?.achievement_probability || 0) > 0.8 ? 'success' :
                          (predictionResult.summary?.achievement_probability || 0) > 0.6 ? 'warning' : 'error'
                        }
                        text={
                          (predictionResult.summary?.achievement_probability || 0) > 0.8 ? 'Low Risk' :
                          (predictionResult.summary?.achievement_probability || 0) > 0.6 ? 'Medium Risk' : 'High Risk'
                        }
                      />
                    </div>
                  </div>
                </Card>
              </Col>
              
              <Col span={12}>
                <Card 
                  title={
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      Strategic Recommendations
                    </span>
                  }
                  style={{ 
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {(() => {
                    const achievementProb = predictionResult.summary?.achievement_probability || 0;
                    const daysAheadBehind = predictionResult.summary?.days_ahead_behind || 0;
                    const recommendations = [];
                    
                    if (achievementProb < 0.7) {
                      recommendations.push({
                        type: 'warning',
                        text: 'Consider increasing resource allocation'
                      });
                      recommendations.push({
                        type: 'info',
                        text: 'Review current strategies for bottlenecks'
                      });
                    } else if (achievementProb > 0.9) {
                      recommendations.push({
                        type: 'success',
                        text: 'Excellent trajectory - maintain strategies'
                      });
                      recommendations.push({
                        type: 'info',
                        text: 'Consider more ambitious targets'
                      });
                    } else {
                      recommendations.push({
                        type: 'success',
                        text: 'Performance on track - continue approach'
                      });
                    }
                    
                    if (daysAheadBehind > 5) {
                      recommendations.push({
                        type: 'error',
                        text: 'Timeline behind - consider acceleration'
                      });
                    } else if (daysAheadBehind < -5) {
                      recommendations.push({
                        type: 'success',
                        text: 'Timeline ahead - excellent progress'
                      });
                    }
                    
                    return recommendations.map((rec, index) => (
                      <Alert
                        key={index}
                        message={rec.text}
                        type={rec.type}
                        showIcon
                        style={{ 
                          marginBottom: '8px',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                      />
                    ));
                  })()}
                </Card>
              </Col>
            </Row>
          )}

          {/* Detailed Summary */}
          {predictionResult.summary && (
            <Card 
              title={
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  Detailed Analysis
                </span>
              }
              style={{ 
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#374151' }}>Timeline Information</h4>
                  <div style={{ marginBottom: '8px' }}>
                    <Text>Performance Trend: </Text>
                    <Text strong>{predictionResult.summary.performance_trend || 'Unknown'}</Text>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <Text>Confidence Level: </Text>
                    <Text strong>{formatPercentage(predictionResult.summary.confidence_level || 0)}</Text>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <Text>Model Used: </Text>
                    <Text strong>{predictionResult.model_used === 'rfr' ? 'Revenue Forecast' : 
                             predictionResult.model_used === 'linear' ? 'Linear Trend' : 
                             predictionResult.model_used === 'ensemble' ? 'Ensemble' : 
                             predictionResult.model_used}</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#374151' }}>Performance Metrics</h4>
                  <div style={{ marginBottom: '8px' }}>
                    <Text>Main Target: </Text>
                    <Text strong>{formatValueByScale(predictionResult.summary.main_target || 0, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}</Text>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <Text>Gap to Target: </Text>
                    <Text strong>{formatValueByScale((predictionResult.summary.main_target || 0) - (predictionResult.summary.predicted_final || 0), selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}</Text>
                  </div>
                </Col>
              </Row>

              {/* Quarterly Summary */}
              {predictionResult.summary.quarterly_summary && predictionResult.summary.quarterly_summary.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ fontWeight: '600', marginBottom: '12px', color: '#374151' }}>Quarterly Breakdown</h4>
                  <Row gutter={16}>
                    {predictionResult.summary.quarterly_summary.map((quarter, index) => (
                      <Col span={6} key={index}>
                        <Card size="small" style={{ 
                          textAlign: 'center',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}>
                          <div style={{ fontWeight: '600', color: '#374151' }}>{quarter.quarter}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{quarter.status}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {formatValueByScale(quarter.current_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)} / {formatValueByScale(quarter.target_value, selectedPlanData?.scale_name, selectedPlanData?.scale_unit_name, selectedPlanData?.scale_unit_symbol)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {formatPercentage(quarter.completion_pct)}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Chart Preview Modal */}
      <Modal
        visible={previewModal.visible}
        title={previewModal.title}
        footer={null}
        onCancel={() => setPreviewModal({ visible: false, image: null, title: '' })}
        width="95%"
        style={{ top: 20 }}
        centered
        bodyStyle={{ 
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#f8fafc'
        }}
      >
        {previewModal.image && (
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
          }}>
            <Image
              src={`data:image/png;base64,${previewModal.image}`}
              alt={previewModal.title}
              style={{ 
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              preview={false}
            />
          </div>
        )}
      </Modal>

      <style jsx>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        @keyframes matrixRain {
          0% {
            transform: translateY(-20px);
            opacity: 1;
          }
          100% {
            transform: translateY(40px);
            opacity: 0;
          }
        }
        
        @keyframes matrixGlow {
          0%, 100% {
            text-shadow: 0 0 5px #064e3b, 0 0 10px #064e3b, 0 0 15px #064e3b;
          }
          50% {
            text-shadow: 0 0 2px #064e3b, 0 0 5px #064e3b, 0 0 8px #064e3b;
          }
        }
        
        .matrix-digit {
          animation: matrixRain 2s linear infinite, matrixGlow 1s ease-in-out infinite;
        }
        
        .matrix-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          animation: matrixSweep 3s ease-in-out infinite;
        }
        
        .matrix-button::after {
          content: '1010110101';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-family: 'Courier New', monospace;
          font-size: 8px;
          color: rgba(255, 255, 255, 0.1);
          letter-spacing: 1px;
          animation: matrixCode 2s linear infinite;
          pointer-events: none;
        }
        
        @keyframes matrixSweep {
          0% { left: -100%; }
          50% { left: 100%; }
          100% { left: 100%; }
        }
        
        @keyframes matrixCode {
          0% { opacity: 0.1; }
          50% { opacity: 0.3; }
          100% { opacity: 0.1; }
        }
      `}</style>
    </div>
  );
}

export default Prediction;
